import React, { useEffect } from 'react';
import { createMap } from 'maplibre-gl-js-amplify';
import { Marker, Popup, LngLatBounds } from 'maplibre-gl';
import { Amplify } from 'aws-amplify';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box } from '@mui/material';

function Map({ devicePositions }) {

    const [mapRefState, setMapRefState] = React.useState(null);
    const mapRef = React.useCallback(node => {
        setMapRefState(node);
    }, []);

    const [map, setMap] = React.useState(null);


    useEffect(() => {
        if (mapRefState && !map) {
            // Get the current Amplify configuration instead of importing amplifyConfig
            const currentConfig = Amplify.getConfig();

            createMap({
                container: mapRefState,
                region: currentConfig.geo?.region,
            }).then(createdMap => {
                setMap(createdMap);
            }).catch(console.error);
        }

        return () => {
            if (map) {
                map.remove();
                setMap(null);
            }
        };
    }, [map, mapRefState]);

    // Add markers in a separate effect
    useEffect(() => {
        if (!map) return;

        // Remove any old markers before adding new ones
        if (map._deviceMarkers) {
            map._deviceMarkers.forEach(marker => marker.remove());
        }

        const markers = [];
        const bounds = new LngLatBounds();

        function normalizePos(pos) {
            if (Array.isArray(pos) && pos.length === 2 && Number.isFinite(pos[0]) && Number.isFinite(pos[1])) {
                return pos;
            }
            if (pos && typeof pos === 'object') {
                if (Number.isFinite(pos.lng) && Number.isFinite(pos.lat)) {
                    return { lng: pos.lng, lat: pos.lat };
                }
                if (Number.isFinite(pos.lon) && Number.isFinite(pos.lat)) {
                    return { lng: pos.lon, lat: pos.lat };
                }
            }
            return null;
        }

        for (const [name, pos] of Object.entries(devicePositions)) {
            const normPos = normalizePos(pos);
            if (normPos) {
                try {
                    const popup = new Popup({ offset: 25 })
                        .setHTML(`<div>${name}</div>`);
                    const marker = new Marker()
                        .setLngLat(normPos)
                        .setPopup(popup)
                        .addTo(map);
                    markers.push(marker);
                    bounds.extend(normPos);
                } catch (error) {
                    console.error("Error adding marker:", error);
                }
            } else {
                console.warn("Skipping invalid pos for", name, pos);
            }
        }

        // Store markers for next cleanup
        map._deviceMarkers = markers;

        if (!bounds.isEmpty()) {
            try {
                map.fitBounds(bounds, {
                    padding: 100,
                    maxZoom: 10,
                });
            } catch (error) {
                console.error("Error fitting map bounds:", error);
            }
        }

        // Cleanup device markers on unmount or next run
        return () => {
            markers.forEach(marker => marker.remove());
        };
    }, [devicePositions, map, mapRefState]);

    return (
        <Box ref={mapRef} sx={{ p: 2, width: '100%', height: '100%' }} />
    );
}

export default Map;